import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationSchema from "_shared/_api/queries/pagination/_schema/pagination-parameters-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import z from "zod";
import { WorkAgreementOrderField } from "../_fields/work-agreement-order-field";

export type WorkAgreementFilterParametersJSON = z.infer<
  typeof WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersJSONZ
>;
export type WorkAgreementFilterParametersParams = z.infer<
  typeof WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ
>;

export type WorkAgreementOrderParametersJSON = z.infer<
  typeof WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ
>;
export type WorkAgreementOrderParametersParams = z.infer<
  typeof WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ
>;

export type WorkAgreementQueryJSON = z.infer<typeof WorkAgreementQuerySchema.WorkAgreementQueryJSONZ>;
export type WorkAgreementQueryParams = z.infer<typeof WorkAgreementQuerySchema.WorkAgreementQueryZ>;

export class WorkAgreementFilterParametersSchema {
  public static readonly type = z.custom<WorkAgreementFilterParameters>(
    (data) => data instanceof WorkAgreementFilterParameters,
    "Not a valid WorkAgreementFilterParameters instance"
  );

  public static readonly WorkAgreementFilterParametersJSONZ = z.object({
    query: z.string().optional(),
    RFPId: z.uuid().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    clientUserId: z.uuid().optional(),
    clientEntityId: z.uuid().optional(),
    clientTeamMemberUserId: z.uuid().optional(),
    clientTeamMemberEntityId: z.uuid().optional(),
    minStart: z.iso.datetime({ offset: true }).optional(),
    maxStart: z.iso.datetime({ offset: true }).optional(),
    hasEnd: z.boolean().optional(),
    minEnd: z.iso.datetime({ offset: true }).optional(),
    maxEnd: z.iso.datetime({ offset: true }).optional(),
    vendorTeamRestricted: z.boolean().optional(),
    vendorUserId: z.uuid().optional(),
    vendorEntityId: z.uuid().optional(),
    vendorTeamMemberUserId: z.uuid().optional(),
    vendorTeamMemberEntityId: z.uuid().optional(),
    additionalVendorUserId: z.uuid().optional(),
    additionalVendorEntityId: z.uuid().optional(),
    feeScheduleCategoryId: z.uuid().optional(),
    feeScheduleCategoryName: z.string().optional(),
    feeScheduleCategoryDescription: z.string().optional(),
    teamTemplateId: z.uuid().optional(),
    teamTemplateName: z.string().optional(),
    feeScheduleTemplateId: z.uuid().optional(),
    feeScheduleTemplateName: z.string().optional(),
    minDiscount: z.number().optional(),
    maxDiscount: z.number().optional(),
    isArchived: z.boolean().optional(),
    maxArchived: z.iso.datetime({ offset: true }).optional(),
    minArchived: z.iso.datetime({ offset: true }).optional(),
    userId: z.uuid().optional(),
  });

  public static readonly WorkAgreementFilterParametersZ = z.object({
    query: z.string().optional(),
    RFPId: z.instanceof(Guid).optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    clientUserId: z.instanceof(Guid).optional(),
    clientEntityId: z.instanceof(Guid).optional(),
    clientTeamMemberUserId: z.instanceof(Guid).optional(),
    clientTeamMemberEntityId: z.instanceof(Guid).optional(),
    minStart: z.instanceof(AHDate).optional(),
    maxStart: z.instanceof(AHDate).optional(),
    hasEnd: z.boolean().optional(),
    minEnd: z.instanceof(AHDate).optional(),
    maxEnd: z.instanceof(AHDate).optional(),
    vendorTeamRestricted: z.boolean().optional(),
    vendorUserId: z.instanceof(Guid).optional(),
    vendorEntityId: z.instanceof(Guid).optional(),
    vendorTeamMemberUserId: z.instanceof(Guid).optional(),
    vendorTeamMemberEntityId: z.instanceof(Guid).optional(),
    additionalVendorUserId: z.instanceof(Guid).optional(),
    additionalVendorEntityId: z.instanceof(Guid).optional(),
    feeScheduleCategoryId: z.instanceof(Guid).optional(),
    feeScheduleCategoryName: z.string().optional(),
    feeScheduleCategoryDescription: z.string().optional(),
    teamTemplateId: z.instanceof(Guid).optional(),
    teamTemplateName: z.string().optional(),
    feeScheduleTemplateId: z.instanceof(Guid).optional(),
    feeScheduleTemplateName: z.string().optional(),
    minDiscount: z.number().optional(),
    maxDiscount: z.number().optional(),
    isArchived: z.boolean().optional(),
    maxArchived: z.instanceof(AHDate).optional(),
    minArchived: z.instanceof(AHDate).optional(),
    userId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): WorkAgreementFilterParametersParams {
    return parseOrThrow(WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ, values, {
      logError: true,
      schemaName: "WorkAgreementFilterParametersSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static deserialize(json: unknown): WorkAgreementFilterParametersParams | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersJSONZ, json, {
      logError: true,
      schemaName: "WorkAgreementFilterParametersSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return this.codec.decode(parsed);
  }

  public static serialize(params: WorkAgreementFilterParametersParams): WorkAgreementFilterParametersJSON {
    const parsed = WorkAgreementFilterParametersSchema.validate(params);
    return this.codec.encode(parsed);
  }

  public static readonly codec = z.codec(
    WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersJSONZ,
    WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersZ,
    {
      decode: (data) => ({
        query: data.query,
        RFPId: Guid.fromJSON(data.RFPId) ?? undefined,
        name: data.name,
        description: data.description,
        clientUserId: Guid.fromJSON(data.clientUserId) ?? undefined,
        clientEntityId: Guid.fromJSON(data.clientEntityId) ?? undefined,
        clientTeamMemberUserId: Guid.fromJSON(data.clientTeamMemberUserId) ?? undefined,
        clientTeamMemberEntityId: Guid.fromJSON(data.clientTeamMemberEntityId) ?? undefined,
        minStart: AHDate.fromJSON(data.minStart) ?? undefined,
        maxStart: AHDate.fromJSON(data.maxStart) ?? undefined,
        hasEnd: data.hasEnd,
        minEnd: AHDate.fromJSON(data.minEnd) ?? undefined,
        maxEnd: AHDate.fromJSON(data.maxEnd) ?? undefined,
        vendorTeamRestricted: data.vendorTeamRestricted,
        vendorUserId: Guid.fromJSON(data.vendorUserId) ?? undefined,
        vendorEntityId: Guid.fromJSON(data.vendorEntityId) ?? undefined,
        vendorTeamMemberUserId: Guid.fromJSON(data.vendorTeamMemberUserId) ?? undefined,
        vendorTeamMemberEntityId: Guid.fromJSON(data.vendorTeamMemberEntityId) ?? undefined,
        additionalVendorUserId: Guid.fromJSON(data.additionalVendorUserId) ?? undefined,
        additionalVendorEntityId: Guid.fromJSON(data.additionalVendorEntityId) ?? undefined,
        feeScheduleCategoryId: Guid.fromJSON(data.feeScheduleCategoryId) ?? undefined,
        feeScheduleCategoryName: data.feeScheduleCategoryName,
        feeScheduleCategoryDescription: data.feeScheduleCategoryDescription,
        teamTemplateId: Guid.fromJSON(data.teamTemplateId) ?? undefined,
        teamTemplateName: data.teamTemplateName,
        feeScheduleTemplateId: Guid.fromJSON(data.feeScheduleTemplateId) ?? undefined,
        feeScheduleTemplateName: data.feeScheduleTemplateName,
        minDiscount: data.minDiscount,
        maxDiscount: data.maxDiscount,
        isArchived: data.isArchived,
        maxArchived: data.maxArchived ? new AHDate(data.maxArchived) : undefined,
        minArchived: data.minArchived ? new AHDate(data.minArchived) : undefined,
        userId: Guid.fromJSON(data.userId) ?? undefined,
      }),
      encode: (params) => ({
        query: params.query,
        RFPId: params.RFPId?.toJSON(),
        name: params.name,
        description: params.description,
        clientUserId: params.clientUserId?.toJSON(),
        clientEntityId: params.clientEntityId?.toJSON(),
        clientTeamMemberUserId: params.clientTeamMemberUserId?.toJSON(),
        clientTeamMemberEntityId: params.clientTeamMemberEntityId?.toJSON(),
        minStart: params.minStart?.toJSON(),
        maxStart: params.maxStart?.toJSON(),
        hasEnd: params.hasEnd,
        minEnd: params.minEnd?.toJSON(),
        maxEnd: params.maxEnd?.toJSON(),
        vendorTeamRestricted: params.vendorTeamRestricted,
        vendorUserId: params.vendorUserId?.toJSON(),
        vendorEntityId: params.vendorEntityId?.toJSON(),
        vendorTeamMemberUserId: params.vendorTeamMemberUserId?.toJSON(),
        vendorTeamMemberEntityId: params.vendorTeamMemberEntityId?.toJSON(),
        additionalVendorUserId: params.additionalVendorUserId?.toJSON(),
        additionalVendorEntityId: params.additionalVendorEntityId?.toJSON(),
        feeScheduleCategoryId: params.feeScheduleCategoryId?.toJSON(),
        feeScheduleCategoryName: params.feeScheduleCategoryName,
        feeScheduleCategoryDescription: params.feeScheduleCategoryDescription,
        teamTemplateId: params.teamTemplateId?.toJSON(),
        teamTemplateName: params.teamTemplateName,
        feeScheduleTemplateId: params.feeScheduleTemplateId?.toJSON(),
        feeScheduleTemplateName: params.feeScheduleTemplateName,
        minDiscount: params.minDiscount,
        maxDiscount: params.maxDiscount,
        isArchived: params.isArchived,
        maxArchived: params.maxArchived?.toJSON(),
        minArchived: params.minArchived?.toJSON(),
        userId: params.userId?.toJSON(),
      }),
    }
  );
}

export class WorkAgreementOrderParametersSchema {
  public static readonly type = z.custom<WorkAgreementOrderParameters>(
    (data) => data instanceof WorkAgreementOrderParameters,
    "Not a valid WorkAgreementOrderParameters instance"
  );
  public static readonly WorkAgreementOrderParametersJSONZ = z.object({
    orderBy: z.enum(WorkAgreementOrderField),
    orderDirection: z.enum(OrderDirection),
  });
  public static readonly WorkAgreementOrderParametersZ = z.object({
    orderBy: z.enum(WorkAgreementOrderField),
    orderDirection: z.enum(OrderDirection),
  });

  public static validate(values: unknown): WorkAgreementOrderParametersParams {
    return parseOrThrow(WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ, values, {
      logError: true,
      schemaName: "WorkAgreementOrderParametersSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static deserialize(json: unknown): WorkAgreementOrderParametersParams | null | undefined {
    const parsed = parseOrThrow(WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ, json, {
      logError: true,
      schemaName: "WorkAgreementOrderParametersSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return WorkAgreementOrderParametersSchema.codec.decode(parsed);
  }

  public static serialize(params: WorkAgreementOrderParametersParams): WorkAgreementOrderParametersJSON {
    const parsed = WorkAgreementOrderParametersSchema.validate(params);
    return parsed;
  }

  public static readonly codec = z.codec(
    WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ,
    WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersZ,
    {
      decode: (data) => ({
        orderBy: data.orderBy,
        orderDirection: data.orderDirection,
      }),
      encode: (params) => ({
        orderBy: params.orderBy,
        orderDirection: params.orderDirection,
      }),
    }
  );
}

export default class WorkAgreementQuerySchema {
  public static readonly WorkAgreementQueryJSONZ = z.object({
    filterParameters: WorkAgreementFilterParametersSchema.WorkAgreementFilterParametersJSONZ.optional(),
    orderParameters: WorkAgreementOrderParametersSchema.WorkAgreementOrderParametersJSONZ.optional(),
    pagination: PaginationSchema.PaginationJSONZ.optional(),
  });

  public static readonly WorkAgreementQueryZ = z.object({
    get filterParameters() {
      return z.instanceof(WorkAgreementFilterParameters).optional();
    },
    get orderParameters() {
      return z.instanceof(WorkAgreementOrderParameters).optional();
    },
    get paginationParameters() {
      return z.instanceof(PaginationParameters).optional();
    },
  });

  public static validate(values: unknown): WorkAgreementQueryParams {
    return parseOrThrow(WorkAgreementQuerySchema.WorkAgreementQueryZ, values, {
      logError: true,
      schemaName: "WorkAgreementQuerySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): WorkAgreementQueryJSON {
    return parseOrThrow(WorkAgreementQuerySchema.WorkAgreementQueryJSONZ, json, {
      logError: true,
      schemaName: "WorkAgreementQuerySchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is WorkAgreementQueryParams {
    try {
      WorkAgreementQuerySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is WorkAgreementQueryJSON {
    try {
      WorkAgreementQuerySchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): WorkAgreementQueryParams | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(WorkAgreementQuerySchema.WorkAgreementQueryJSONZ, json, {
      logError: true,
      schemaName: "WorkAgreementQuerySchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return WorkAgreementQuerySchema.codec.decode(parsed);
  }

  public static serialize(params: WorkAgreementQueryParams): WorkAgreementQueryJSON {
    if (params === null || params === undefined) return params as WorkAgreementQueryJSON;
    const parsed = WorkAgreementQuerySchema.validate(params);
    if (parsed === null || parsed === undefined) return parsed as WorkAgreementQueryJSON;
    return WorkAgreementQuerySchema.codec.encode(parsed);
  }

  public static readonly codec = z.codec(
    WorkAgreementQuerySchema.WorkAgreementQueryJSONZ,
    WorkAgreementQuerySchema.WorkAgreementQueryZ,
    {
      decode: (data) => ({
        filterParameters: WorkAgreementFilterParameters.fromJSON(data.filterParameters) ?? undefined,
        orderParameters: WorkAgreementOrderParameters.fromJSON(data.orderParameters) ?? undefined,
        paginationParameters: PaginationParameters.fromJSON(data.pagination),
      }),
      encode: (params) => ({
        filterParameters: params.filterParameters ? params.filterParameters.toJSON() : undefined,
        orderParameters: params.orderParameters ? params.orderParameters.toJSON() : undefined,
        pagination: params.paginationParameters ? params.paginationParameters.toJSON() : undefined,
      }),
    }
  );
}
